// deps
import React, { useMemo, useCallback, useContext } from "react";

// internal-deps
import { SESSION_STORAGE_KEYS, useEffectOnce } from "@swiggy-private/core-ui";

// contexts
import { JuspayContext } from "@payment-client/contexts/JuspayContext";
import { ShowPaymentContext, ShowPaymentContextValue } from "@payment-client/contexts/ShowPaymentContext";
import { WalletBalanceContext } from "@payment-client/contexts/WalletBalanceContext";
import { PaymentPayContext, PaymentPayContextValue } from "@payment-client/contexts/PaymentPayContext";
import { CardBalanceContext } from "@payment-client/contexts/CardBalanceContext";
import { AmazonPaySdkContext } from "@payment-client/contexts/AmazonPaySdkContext";
import {
    useSessionStorage,
    recordNREvent,
    NEWRELIC_TABLES,
    WP_WALLET_BALANCE_STATE,
} from "@swiggy-private/checkout-core";
import { CredEligiblityContext } from "@payment-client/contexts/CredEligiblityContext";
import { UpiIntentContext, UpiIntentContextValue } from "@payment-client/contexts/UpiIntentContext";
import { NetbankingMoreBanksContext } from "@payment-client/contexts/NetbankingMoreBanksContext";
import { PhonepeContext } from "@payment-client/contexts/PhonepeSdkContext";
import { PaymentGroupShowMoreCtaContext } from "@payment-client/contexts/PaymentGroupShowMoreCtaContext";
import { PaylaterLinkedContext } from "@payment-client/contexts/PaylaterLinkedContext";
import { PaymentLoaderContext } from "@payment-client/contexts/PaymentLoaderContext";
import { InAppUpiContext } from "@payment-client/contexts/InAppUpiContext";
import { EmiSelectionProvider } from "@payment-client/components/emi-selection/EmiSelectionProvider";
import { BillImageHalfCardProvider } from "@payment-client/contexts/BillImageHalfCardContext";

// components
import { OrderContextProvider } from "../components/order-context-provider";
import { LinkWalletContextProvider } from "../components/link-wallet-context-provider";
import { ExpenseCodeHalfCardProvider } from "../components/expense-code-provider-halfcard";

// hooks
import useJuspaySdk from "@payment-client/hooks/UseJuspaySdk";
import { useWalletBalance } from "@payment-client/hooks/UseWalletBalance";
import { useCardBalance } from "@payment-client/hooks/UseCardBalance";
import useAmazonPaySdk from "@payment-client/hooks/useAmazonPaySdk";
import useMoreBanks from "@payment-client/hooks/UseMoreBanks";
import UsePhonepeSdkAvailable from "@payment-client/hooks/UsePhonepeSdkAvailable";
import { usePaymentGroupShowMoreCta } from "@payment-client/hooks/UsePaymentGroupShowMoreCta";
import useSplitPayState from "@payment-client/hooks/UseSplitpayState";
import { usePayLaterLinkedStatus } from "@payment-client/hooks/UsePayLaterLinkedStatus";
import useCredEligible from "@payment-client/hooks/UseCredEligible";
import { usePaymentLoader } from "@payment-client/hooks/UsePaymentLoader";
import useUpiMethodsAvailable from "@payment-client/hooks/useUpiMethodsAvailable";
import { useInAppUpi } from "@payment-client/hooks/UseInAppUpi";

// helpers
import { getPaymentAmount } from "@payment-client/helpers/PaymentHelper";
import { getPaymentPageOfferEnabledVal } from "@payment-client/helpers/PaymentOffersHelper";
import {
    getJuspaySdkFeatureAvailability,
    resolveClientAuthTokenDataForMerchantId,
    resolveMerchantId,
} from "@payment-client/helpers/JuspayHelpers";
import { VALID_PAYMENT_GROUP_TYPES } from "@swiggy-private/payment-types";
import { getUpiIntentMethods } from "@payment-client/helpers/UpiHelper";
import { getSelectedSplitPayMethods } from "@payment-client/helpers/SplitPay";
import { ThemeProvider } from "@swiggy-private/dls-web-theme/dist/components/ThemeProvider";
import { useTheme } from "@swiggy-private/dls-web-theme/dist/components/ThemeProvider/use-theme";
import { SplitPayListContext } from "@payment-client/contexts/SplitPayListContext";

interface WithContextProvidersProps {
    paymentResponse: NonNullable<ShowPaymentContextValue["paymentResponse"]>;
    children: React.ReactNode;
    isTurboPaymentMethod: boolean;
    forceColorMode?: "light" | "dark";
}

export const WithContextProviders = ({
    paymentResponse,
    children,
    isTurboPaymentMethod,
    forceColorMode,
}: WithContextProvidersProps): React.ReactElement => {
    const [withCoupon, setWithCoupon] = useSessionStorage(
        SESSION_STORAGE_KEYS.WITH_COUPON,
        paymentResponse.couponApplied,
    );

    // payment group show more cta
    const paymentGroupShowMoreCtaContextValue = usePaymentGroupShowMoreCta();

    // juspay
    const juspayContextValue = useJuspaySdk(paymentResponse);

    const isSdkAvailableForInAppUpi = useMemo(
        () =>
            getJuspaySdkFeatureAvailability(
                VALID_PAYMENT_GROUP_TYPES.INAPP_UPI,
                juspayContextValue.isSdkAvailable,
                juspayContextValue.featuresAvailability,
            ),
        [juspayContextValue.isSdkAvailable, juspayContextValue.featuresAvailability],
    );

    const inAppUpiContextValue = useInAppUpi(paymentResponse, isSdkAvailableForInAppUpi);

    // upi intent methods
    const upiIntentMethodsList = useMemo(() => getUpiIntentMethods(paymentResponse), [paymentResponse]);
    const { availableUpiIntentMethods, upiIntentMethodFetchInProgress } = useUpiMethodsAvailable(
        upiIntentMethodsList,
        isTurboPaymentMethod,
        paymentResponse,
    );
    const upiIntentValue = useMemo(
        (): UpiIntentContextValue => ({
            availableMethods: availableUpiIntentMethods,
            upiIntentMethodFetchInProgress,
        }),
        [availableUpiIntentMethods, upiIntentMethodFetchInProgress],
    );
    const { withSplitPay, setSplitPayMethods, splitPayMethods, showSplitPay, splitPayList } = useSplitPayState(
        paymentResponse,
        withCoupon,
        availableUpiIntentMethods,
    );
    const splitPayListContextValue = useMemo(
        () => ({
            splitPayList,
        }),
        [splitPayList],
    );

    // wallet
    const walletBalanceContextValue = useWalletBalance({ paymentResponse });

    // (2.10) Denominator for wallet observability: which wallets the user was offered at render time.
    // Per-wallet balance & sufficiency is captured lazily in wp_wallet_balance_fetch_success, so it is
    // intentionally not duplicated here (balances aren't resolved yet at mount).
    useEffectOnce(() => {
        const walletCodes = Object.keys(walletBalanceContextValue.state || {});
        if (walletCodes.length === 0) {
            return;
        }
        recordNREvent({
            eventType: NEWRELIC_TABLES.WP_WALLET_BALANCE,
            state: WP_WALLET_BALANCE_STATE.PAYMENT_SCREEN_RENDER,
            walletCodes: walletCodes.join(","),
            walletCount: walletCodes.length,
        });
    });

    // paylater
    const paylaterLinkedContextValue = usePayLaterLinkedStatus(paymentResponse);

    // card
    const cardBalanceContextValue = useCardBalance({ paymentResponse, isTurboPaymentMethod });

    // netbanking more banks
    const netbankingMoreBanksContextValue = useMoreBanks(isTurboPaymentMethod, paymentResponse);

    // payment
    const _withCoupon = withCoupon && paymentResponse.couponApplied === true;
    const _withSplitPay = withSplitPay && showSplitPay;
    const _setWithCoupon = useCallback(
        (use: boolean) => {
            setWithCoupon(use);
            const selectedSplitPayList = getSelectedSplitPayMethods(splitPayList);
            setSplitPayMethods(selectedSplitPayList);
        },
        [splitPayList, setWithCoupon, setSplitPayMethods],
    );

    const billTotal = useMemo(
        () => Number(getPaymentAmount(paymentResponse, _withCoupon).unwrapOr("")) || 0,
        [paymentResponse, _withCoupon],
    );
    const amountToPay = useMemo(
        () => Number(getPaymentAmount(paymentResponse, _withCoupon, _withSplitPay).unwrapOr("")) || 0,
        [paymentResponse, _withCoupon, _withSplitPay],
    );
    const amountToPayWithoutCouponWithoutSplitpay = useMemo(
        () => Number(getPaymentAmount(paymentResponse, false, false).unwrapOr("")) || 0,
        [paymentResponse],
    );

    // CRED
    const isSdkAvailableForCred = useMemo(
        () =>
            getJuspaySdkFeatureAvailability(
                VALID_PAYMENT_GROUP_TYPES.CRED,
                juspayContextValue.isSdkAvailable,
                juspayContextValue.featuresAvailability,
            ),
        [juspayContextValue.isSdkAvailable, juspayContextValue.featuresAvailability],
    );

    //paymentOffers
    const paymentPageOfferEnabled = useMemo(() => getPaymentPageOfferEnabledVal(paymentResponse), [paymentResponse]);

    const { _rawPaymentResponse: contextRawPaymentResponse } = useContext(ShowPaymentContext);

    const merchantId = resolveMerchantId(contextRawPaymentResponse, VALID_PAYMENT_GROUP_TYPES.CRED);
    const clientAuthToken = resolveClientAuthTokenDataForMerchantId(
        juspayContextValue.clientAuthTokenData,
        merchantId,
        paymentResponse?.juspayMeta?.merchantId,
    )?.clientAuthToken;

    const credContextValue = useCredEligible({
        paymentResponse,
        clientAuthToken,
        merchantId,
        isSdkAvailable: isSdkAvailableForCred,
        withSplitPay,
        amountToPay: String(amountToPay),
    });

    // Amazon Pay SDK
    const amazonPaySdkContextValue = useAmazonPaySdk(paymentResponse, amountToPay, isTurboPaymentMethod);

    // Phonepe SDK
    const isPhonepeSdkAvailable = UsePhonepeSdkAvailable();
    const phonepeSdkContextValue = useMemo(
        () => ({
            isPhonepeSdkAvailable,
        }),
        [isPhonepeSdkAvailable],
    );

    // loading state
    const paymentLoaderContextValue = usePaymentLoader();

    const rewardsEnabled = useMemo(() => paymentResponse?.rewardsEnabled || false, [paymentResponse?.rewardsEnabled]);

    const paymentPayContextValue = useMemo<PaymentPayContextValue>(
        () => ({
            billTotal,
            amountToPay,
            paymentPageOfferEnabled,
            amountToPayWithoutCouponWithoutSplitpay,
            showSplitPay,
            selectedSplitPayMethods: splitPayMethods,
            setSplitPayMethods,
            setWithCoupon: _setWithCoupon,
            withSplitPay: _withSplitPay,
            withCoupon: _withCoupon,
            rewardsEnabled,
        }),
        [
            billTotal,
            amountToPay,
            paymentPageOfferEnabled,
            amountToPayWithoutCouponWithoutSplitpay,
            showSplitPay,
            splitPayMethods,
            setSplitPayMethods,
            _setWithCoupon,
            _withSplitPay,
            _withCoupon,
            rewardsEnabled,
        ],
    );
    const theme = useTheme();

    return (
        // ThemeProvider(with force light color mode) is needed to set the payments flow to be in light theme only temporarily to support scenes cart which will be in dark theme
        <ThemeProvider theme={theme} colorMode={forceColorMode || "light"} design_language={theme.designLanguage}>
            <PaymentLoaderContext.Provider value={paymentLoaderContextValue}>
                <WalletBalanceContext.Provider value={walletBalanceContextValue}>
                    <PaylaterLinkedContext.Provider value={paylaterLinkedContextValue}>
                        <PhonepeContext.Provider value={phonepeSdkContextValue}>
                            <AmazonPaySdkContext.Provider value={amazonPaySdkContextValue}>
                                <CardBalanceContext.Provider value={cardBalanceContextValue}>
                                    <JuspayContext.Provider value={juspayContextValue}>
                                        <InAppUpiContext.Provider value={inAppUpiContextValue}>
                                            <UpiIntentContext.Provider value={upiIntentValue}>
                                                <NetbankingMoreBanksContext.Provider
                                                    value={netbankingMoreBanksContextValue}
                                                >
                                                    <PaymentGroupShowMoreCtaContext.Provider
                                                        value={paymentGroupShowMoreCtaContextValue}
                                                    >
                                                        <PaymentPayContext.Provider value={paymentPayContextValue}>
                                                            <SplitPayListContext.Provider
                                                                value={splitPayListContextValue}
                                                            >
                                                                <CredEligiblityContext.Provider
                                                                    value={credContextValue}
                                                                >
                                                                    <ExpenseCodeHalfCardProvider>
                                                                        <BillImageHalfCardProvider>
                                                                            <OrderContextProvider>
                                                                                <LinkWalletContextProvider>
                                                                                    <EmiSelectionProvider>
                                                                                        {children}
                                                                                    </EmiSelectionProvider>
                                                                                </LinkWalletContextProvider>
                                                                            </OrderContextProvider>
                                                                        </BillImageHalfCardProvider>
                                                                    </ExpenseCodeHalfCardProvider>
                                                                </CredEligiblityContext.Provider>
                                                            </SplitPayListContext.Provider>
                                                        </PaymentPayContext.Provider>
                                                    </PaymentGroupShowMoreCtaContext.Provider>
                                                </NetbankingMoreBanksContext.Provider>
                                            </UpiIntentContext.Provider>
                                        </InAppUpiContext.Provider>
                                    </JuspayContext.Provider>
                                </CardBalanceContext.Provider>
                            </AmazonPaySdkContext.Provider>
                        </PhonepeContext.Provider>
                    </PaylaterLinkedContext.Provider>
                </WalletBalanceContext.Provider>
            </PaymentLoaderContext.Provider>
        </ThemeProvider>
    );
};
